import { RefObject } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNotification, useTranslate } from "@refinedev/core";

import { ModalRef } from "@shared/components";
import { useEditVoucherMutation } from "@modules/vouchers/services/mutations";
import { VoucherFormValues, voucherSchema } from "@modules/vouchers/validations";
import { listVouchersQueryKey } from "@modules/vouchers/services/queries/listVouchers/query";

export function useEditVoucherModal(modalRef: RefObject<ModalRef>, onComplete?: () => void) {
  const queryClient = useQueryClient();
  const translate = useTranslate();
  const { open } = useNotification();

  const methods = useForm<VoucherFormValues>({
    resolver: zodResolver(voucherSchema()),
    mode: "onBlur",
  });

  const { mutateAsync, isPending } = useEditVoucherMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listVouchersQueryKey,
        refetchType: 'all',
      });

      open?.({
        type: "success",
        message: translate("voucher_batch_admin.success.edit"),
      });

      onComplete?.();
      modalRef.current?.close();
    },
  });

  const handleSubmit = async (data: VoucherFormValues) => {
    const voucherId = modalRef.current?.getId();
    await mutateAsync({ id: voucherId as string, data });
  }

  return {
    methods,
    isPending,
    handleSubmit,
  };
}
